import { Fragment } from 'react';
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Button, CollapseProps, Divider, Flex, Typography } from 'antd';
import classNames from 'classnames';

import { UserDetails } from '@Types/common/UserDetails';
import { DependentDetails } from '@Types/common/DependentDetails';
import { ReactComponent as ChevroneDownIcon } from '@Icons/additional/chevron-down-blue.svg';
import { ReactComponent as EditBlue } from '@Icons/additional/edit-blue.svg';
import { MCollapse } from '@Components/Base';
import {
  useGetDependentDetailsQuery,
  useGetEmergencyContactsQuery,
  useGetEmployeeDetailsQuery,
} from '@Store/api/userApiSlice';
import { useAppSelector } from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { EmergencyContact } from '@Types/common/EmergencyContact';

import styles from './details.module.css';
import {
  prepareDependentDetails,
  prepareEmergencyContact,
  prepareEmployeeDetails,
} from './utils';

const RenderIcon: CollapseProps['expandIcon'] = ({ isActive }) => (
  <ChevroneDownIcon
    className={styles.expandIcon}
    style={{ transform: `rotate(${isActive ? 180 : 0}deg)` }}
  />
);

type DetailsCardItem = {
  title: string;
  description: string;
};

const getDetailsCard = (
  title: string,
  items: DetailsCardItem[],
  isEditable: boolean,
  isMobile: boolean
) => {
  const dividerEl = <Divider className={styles.cardLineDivider} />;

  return {
    id: title.toUpperCase(),
    label: title,
    children: items.map((item, index) => (
      <Fragment key={item.title}>
        {(isMobile && index === 0) || !isMobile ? dividerEl : null}
        <Flex className={styles.detailsCardItem}>
          <Typography.Text className={styles.detailsCardItemTitle}>
            {item.title}
          </Typography.Text>
          <Typography.Text className={styles.detailsCardItemDescription}>
            {item.description}
          </Typography.Text>
        </Flex>
      </Fragment>
    )),
    extra: isEditable ? (
      <>
        <Button
          type="text"
          className={styles.detailsCardEditButton}
          icon={<EditBlue />}
        />
        <Divider type="vertical" className={styles.detailsCardDivider} />
      </>
    ) : null,
  };
};

interface DetailsCardsProps {
  employeeDetails: UserDetails | undefined;
  emergencyContact: EmergencyContact | undefined;
  dependentDetails: DependentDetails[] | undefined;
  phone: string;
  isMobile: boolean;
}

const DetailsCards: React.FC<DetailsCardsProps> = ({
  emergencyContact,
  employeeDetails,
  dependentDetails,
  phone,
  isMobile,
}) => {
  const employeeDetailsCard = prepareEmployeeDetails(employeeDetails, phone);
  const dependentDetailsCard = prepareDependentDetails(dependentDetails);
  const emergencyContactDetailsCard = prepareEmergencyContact(emergencyContact);

  const collapseItems = [
    getDetailsCard('Employee Details', employeeDetailsCard, false, isMobile),
    getDetailsCard('Dependent Details', dependentDetailsCard, true, isMobile),
    getDetailsCard(
      'Emergency Contact Details',
      emergencyContactDetailsCard,
      true,
      isMobile
    ),
  ];

  return (
    <MCollapse
      collapsible="icon"
      className={classNames(styles.detailsCards, isMobile ? 'mobile' : null)}
      expandIcon={RenderIcon}
      items={collapseItems}
    />
  );
};

interface DetailsProps {
  isMobile: boolean;
}

const Details: React.FC<DetailsProps> = ({ isMobile }) => {
  const { empNumber, contact } = useAppSelector(currentUserSelector);

  const { data: emergencyContact } = useGetEmergencyContactsQuery(empNumber, {
    skip: !empNumber,
  });
  const { data: dependentDetails } = useGetDependentDetailsQuery(empNumber, {
    skip: !empNumber,
  });
  const { data: employeeDetails } = useGetEmployeeDetailsQuery(empNumber, {
    skip: !empNumber,
  });

  return (
    <Flex vertical className={styles.wrapper}>
      <DetailsCards
        emergencyContact={emergencyContact}
        employeeDetails={employeeDetails}
        dependentDetails={dependentDetails}
        phone={contact || ''}
        isMobile={isMobile}
      />
    </Flex>
  );
};

export default Details;
